import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, Outlet } from 'react-router-dom'
import { UserProvider } from './context/UserContext'
import { DhamLangProvider } from './context/ManaunaDhamLangContext'
import Header from './components/Header'
import Footer from './components/Footer'
import Home from './components/Home'
import ManaunaDham from './components/ManaunaDham'
import About from './components/About'
import Contact from './components/Contact'
import Rooms from './components/Rooms'
import RoomDetails from './components/RoomDetails'
const Login = lazy(() => import('./components/Login'))
import Bookings from './components/Bookings'
import Profile from './components/Profile'
import VendorSignup from './components/VendorSignup'
import VendorLogin from './components/VendorLogin'
import VendorForgotPassword from './components/VendorForgotPassword'
import FAQ from './components/FAQ'
import Terms from './components/Terms'
import Refund from './components/Refund'
import BookingPaymentReturn from './components/booking/BookingPaymentReturn'
import ServicesPage from './components/pages/ServicesPage'
import ServiceDetailPage from './components/pages/ServiceDetailPage'
import PackagesPage from './components/pages/PackagesPage'
import VendorsPage from './components/pages/VendorsPage'
import VendorDetailPage from './components/pages/VendorDetailPage'
import BlogsPage from './components/pages/BlogsPage'
import BlogDetailPage from './components/pages/BlogDetailPage'
import GalleryPage from './components/pages/GalleryPage'
import AdminConsole from './admin/AdminConsole'


const AccommodationsPage = lazy(() => import('./components/manauna-dham/AccommodationsPage'))
const FoodPage = lazy(() => import('./components/manauna-dham/FoodPage'))
const TransportPage = lazy(() => import('./components/manauna-dham/TransportPage'))
const PatientTokenPage = lazy(() => import('./components/manauna-dham/PatientTokenPage'))
const ShyamJalPage = lazy(() => import('./components/manauna-dham/ShyamJalPage'))
const LiveDarshanPage = lazy(() => import('./components/manauna-dham/LiveDarshanPage'))
const KhatuShyamPage = lazy(() => import('./components/manauna-dham/KhatuShyamPage'))
const MahantPage = lazy(() => import('./components/manauna-dham/MahantPage'))
const ContactDhamPage = lazy(() => import('./components/manauna-dham/ContactDhamPage'))

const DhamSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>}>
    {children}
  </Suspense>
)

function ManaunaDhamLayout() {
  return (
    <DhamLangProvider>
      <DhamSuspense>
        <Outlet />
      </DhamSuspense>
    </DhamLangProvider>
  )
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

function MainSiteLayout() {
  const location = useLocation()
  const isAuthShellPage =
    location.pathname === '/auth/login' ||
    location.pathname === '/booking/login' ||
    location.pathname === '/booking/forget-password' ||
    location.pathname === '/vendor/login' ||
    location.pathname === '/vendor/forget-password' ||
    location.pathname === '/vendor/signup'

  return (
    <div className="min-h-screen bg-gray-50">
      <ScrollToTop />
      {!isAuthShellPage && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/manauna-dham" element={<ManaunaDhamLayout />}>
            <Route index element={<ManaunaDham />} />
            <Route path="accommodations" element={<AccommodationsPage />} />
            <Route path="food" element={<FoodPage />} />
            <Route path="transport" element={<TransportPage />} />
            <Route path="patient-token" element={<PatientTokenPage />} />
            <Route path="shyam-jal" element={<ShyamJalPage />} />
            <Route path="live-darshan" element={<LiveDarshanPage />} />
            <Route path="khatu-shyam" element={<KhatuShyamPage />} />
            <Route path="mahant" element={<MahantPage />} />
            <Route path="contact" element={<ContactDhamPage />} />
          </Route>
          <Route path="/manona-dham" element={<Navigate to="/manauna-dham" replace />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/rooms" element={<Rooms />} />
          <Route path="/room/:id" element={<RoomDetails />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:id" element={<ServiceDetailPage />} />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/vendors/:username" element={<VendorDetailPage />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/blogs/:slug" element={<BlogDetailPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/booking/payment-return" element={<BookingPaymentReturn />} />
          <Route path="/faqs" element={<FAQ />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/auth/login" element={<Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>}><Login /></Suspense>} />
          <Route path="/booking/login" element={<VendorLogin />} />
          <Route path="/booking/forget-password" element={<VendorForgotPassword />} />
          <Route path="/vendor/login" element={<VendorLogin />} />
          <Route path="/vendor/forget-password" element={<VendorForgotPassword />} />
          <Route path="/bookings" element={<Bookings />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/vendor/signup" element={<VendorSignup />} />
        </Routes>
      </main>
      {!isAuthShellPage && <Footer />}
    </div>
  )
}

function AppContent() {
  return (
    <Routes>
      <Route path="/admin-new/*" element={<AdminConsole />} />
      <Route path="/*" element={<MainSiteLayout />} />
    </Routes>
  )
}

function App() {
  // import.meta.env.BASE_URL is only for Vite asset URLs (/vite-build/...). Laravel serves
  // index.html at the site root (/), so routes are /, /rooms, etc. — never use BASE_URL as basename.
  const raw = import.meta.env.VITE_ROUTER_BASENAME
  const basename =
    typeof raw === 'string' && raw.length > 0 ? raw.replace(/\/$/, '') : undefined
  return (
    <UserProvider>
      <Router basename={basename}>
        <AppContent />
      </Router>
    </UserProvider>
  )
}

export default App
